import React from 'react';
import PropTypes from 'prop-types';
import { capitalize } from 'lodash';
import { getCompoundSentenceList, getListItems } from '@format/list';
import { costFormatter } from '@format/currency';
import { PAYMENT_METHOD_IMAGES } from '../../payment/constants';
import { HALF_PORTION, FREQUENCIES } from '../../products/constants';
import { getPlanFreeItems } from '../PricingList/functions';
import { SummaryItem } from './SummaryItem';
import {
  StyledConfirmationOrderSummary,
  OrderSummaryTitle,
  OngoingSubscriptionTitle,
  Section,
  SubsectionColumn,
  ExpectedDeliveryWrapper,
  SectionTitle,
  SectionSubtitle,
  SectionBody,
  PaymentMethodInfo,
} from './styles.js';

const subscriptionfilters = {
  frequencyInWeeks: {
    label: 'Frequency',
    content: n => (n < 2 ? `Once a week` : `Every ${n} weeks`),
  },
  deliveryNext: { label: 'Next Delivery', content: date => date },
};
const recipeFilters = {
  fresh: { label: 'Fresh Recipes', content: getCompoundSentenceList },
  dry: { label: 'Baked Recipes', content: getCompoundSentenceList },
};

export const ConfirmationOrderSummary = ({
  orders,
  deliveryAddress,
  cardNumberLastFour,
  cardBrand,
  wallet,
  deliveryDate,
  orderTotalCost,
  orderSubtotal,
  orderTotalTax,
  orderSummaryTitle,
  period,
  plans,
  pricingList,
}) => {
  const { street, city, state, zip } = deliveryAddress;
  const starterBoxes = [];
  const subscriptions = [];

  const paymentMethodImage =
    PAYMENT_METHOD_IMAGES[wallet || cardBrand.toLowerCase()];

  for (let [index, order] of orders.entries()) {
    const {
      petName,
      breeds,
      price,
      discountedPrice,
      pricePerPeriod,
      recipes,
      addons,
      portionSize,
      weeksOfFood,
    } = order;

    const pupSummaryTitle = `${FREQUENCIES[weeksOfFood]}-week Starter Box`;

    orderSummaryTitle =
      orders.length > 1 ? 'Your Starter Box Orders' : 'Your Starter Box Order';

    const { fresh, dry } = recipes;
    const count = index + 1;
    const backgroundColor = count % 2 ? 'hero.blueberry' : 'secondary.carrot';

    const startBoxSummaryList = getListItems(recipes, recipeFilters).filter(
      ({ content }) => content !== '',
    );
    const subscriptionSummaryList = getListItems(order, subscriptionfilters);
    if (fresh?.length > 0 && dry?.length > 0) {
      startBoxSummaryList.unshift({
        label: 'Meal Plan',
        content: 'Mixed Bowl Plan',
      });
    } else if (fresh?.length > 0 && portionSize === HALF_PORTION) {
      startBoxSummaryList.unshift({
        label: 'Meal Plan',
        content: 'Half Fresh Plan',
      });
    } else if (fresh?.length > 0) {
      startBoxSummaryList.unshift({
        label: 'Meal Plan',
        content: 'Full Fresh Plan',
      });
    } else if (dry?.length > 0) {
      startBoxSummaryList.unshift({
        label: 'Meal Plan',
        content: 'Full Baked Plan',
      });
    }

    starterBoxes.push(
      <SummaryItem
        key={`starterBox-${index}`}
        title={`${petName}'s ${pupSummaryTitle}`}
        breeds={breeds}
        backgroundColor={backgroundColor}
        summaryList={startBoxSummaryList}
        addons={addons}
        price={price}
        discountedPrice={discountedPrice}
        starterBox
      />,
    );
    subscriptions.push(
      <SummaryItem
        key={`subscriptions-${index}`}
        title={`${petName}'s Subscription`}
        breeds={breeds}
        backgroundColor={backgroundColor}
        summaryList={subscriptionSummaryList}
        pricePerPeriod={pricePerPeriod}
        period={period}
        addons={addons}
        subscription
      />,
    );
  }

  const ConfirmationFreeItems = ({ pricingList, plans }) => {
    const planFreeItems = getPlanFreeItems(plans);

    return (
      <>
        {pricingList?.freeItems?.map((option, key) => (
          <Section
            key={key}
            columns={2}
            hideBorderBottom
            smallMarginPadding
            firstMarginTop={key === 0 ? true : false}
          >
            <SectionSubtitle>
              {`${option.label + ' '}`}
              {plans.length > 1 ? `X${plans.length}` : null}
            </SectionSubtitle>
            <SectionSubtitle value color="status.successPrimary">
              FREE
            </SectionSubtitle>
          </Section>
        ))}
        {planFreeItems.puptainerCount === 0 ? null : (
          <Section
            columns={2}
            hideBorderBottom
            smallMarginPadding
            firstMarginTop={!pricingList?.freeItems?.length}
          >
            <SectionSubtitle>
              {pricingList.puptainerLabel}{' '}
              {planFreeItems.puptainerCount > 1
                ? `X${planFreeItems.puptainerCount}`
                : null}
            </SectionSubtitle>
            <SectionSubtitle value color="status.successPrimary">
              FREE
            </SectionSubtitle>
          </Section>
        )}
        {planFreeItems.scooperCount === 0 ? null : (
          <Section columns={2} hideBorderBottom smallMarginPadding>
            <SectionSubtitle>
              {pricingList.scooperLabel}{' '}
              {planFreeItems.scooperCount > 1
                ? `X${planFreeItems.scooperCount}`
                : null}
            </SectionSubtitle>
            <SectionSubtitle value color="status.successPrimary">
              FREE
            </SectionSubtitle>
          </Section>
        )}
        {planFreeItems.bowlCount === 0 ? null : (
          <Section columns={2} hideBorderBottom smallMarginPadding>
            <SectionSubtitle>
              {pricingList.bowlLabel}{' '}
              {planFreeItems.bowlCount > 1
                ? `X${planFreeItems.bowlCount}`
                : null}
            </SectionSubtitle>
            <SectionSubtitle value color="status.successPrimary">
              FREE
            </SectionSubtitle>
          </Section>
        )}
        <Section columns={2} smallMarginPadding lastPaddingBottom>
          <SectionSubtitle>{pricingList.shippingLabel}</SectionSubtitle>
          <SectionSubtitle value color="status.successPrimary">
            FREE
          </SectionSubtitle>
        </Section>
      </>
    );
  };

  return (
    <StyledConfirmationOrderSummary>
      <OrderSummaryTitle>{orderSummaryTitle}</OrderSummaryTitle>
      {starterBoxes}
      {pricingList && plans && (
        <ConfirmationFreeItems pricingList={pricingList} plans={plans} />
      )}
      {pricingList && (
        <Section columns={2} hideBorderBottom smallMarginPadding firstMarginTop>
          <SectionSubtitle
            heading
            color="status.successPrimary"
          >{`${pricingList.discountLabel} (${pricingList.discountText})`}</SectionSubtitle>
          <SectionSubtitle
            value
            color="status.successPrimary"
          >{`-${costFormatter(pricingList.discountAmount)}`}</SectionSubtitle>
        </Section>
      )}
      <Section
        columns={2}
        hideBorderBottom
        smallMarginPadding
        firstMarginTop={!pricingList}
      >
        <SectionSubtitle>Subtotal:</SectionSubtitle>
        <SectionSubtitle value>{orderSubtotal}</SectionSubtitle>
      </Section>
      {!!orderTotalTax && (
        <Section columns={2} hideBorderBottom smallMarginPadding>
          <SectionSubtitle>Taxes:</SectionSubtitle>
          <SectionSubtitle value>{orderTotalTax}</SectionSubtitle>
        </Section>
      )}
      <Section columns={2} smallMarginPadding lastPaddingBottom>
        <SectionTitle>Total</SectionTitle>
        <SectionTitle>{orderTotalCost}</SectionTitle>
      </Section>
      <Section>
        <SectionTitle>Billing Information</SectionTitle>
        <SectionBody>
          <PaymentMethodInfo>
            {paymentMethodImage}
            {capitalize(cardBrand)} ending in •••• {cardNumberLastFour}
          </PaymentMethodInfo>
        </SectionBody>
      </Section>
      <Section columns={2} mobileStacked>
        <SubsectionColumn left>
          <SectionTitle>Delivery Address</SectionTitle>
          <SectionBody>{street}</SectionBody>
          <SectionBody>
            {city} {state} {zip}
          </SectionBody>
        </SubsectionColumn>
        <SubsectionColumn>
          <ExpectedDeliveryWrapper>
            <SectionTitle>Expected Delivery</SectionTitle>
            <SectionBody>{deliveryDate}</SectionBody>
          </ExpectedDeliveryWrapper>
        </SubsectionColumn>
      </Section>
      <OngoingSubscriptionTitle>Ongoing Subscription</OngoingSubscriptionTitle>
      {subscriptions}
    </StyledConfirmationOrderSummary>
  );
};

ConfirmationOrderSummary.displayName = 'ConfirmationOrderSummary';
ConfirmationOrderSummary.propTypes = {
  orderSummaryTitle: PropTypes.string,
  orders: PropTypes.arrayOf(
    PropTypes.shape({
      petName: PropTypes.string.isRequired,
      breeds: PropTypes.arrayOf(PropTypes.string.isRequired),
      price: PropTypes.string.isRequired,
      discountedPrice: PropTypes.string,
      pricePerPeriod: PropTypes.string.isRequired,
      weeksOfFood: PropTypes.number.isRequired,
      deliveryNext: PropTypes.string.isRequired,
      recipes: PropTypes.shape({
        fresh: PropTypes.arrayOf(PropTypes.string),
        dry: PropTypes.arrayOf(PropTypes.string),
      }).isRequired,
      addons: PropTypes.array,
      portionSize: PropTypes.string,
    }),
  ).isRequired,
  deliveryAddress: PropTypes.shape({
    street: PropTypes.string.isRequired,
    city: PropTypes.string.isRequired,
    state: PropTypes.string.isRequired,
    zip: PropTypes.string.isRequired,
  }).isRequired,
  cardNumberLastFour: PropTypes.string.isRequired,
  cardBrand: PropTypes.string.isRequired,
  wallet: PropTypes.oneOf(['apple_pay', 'google_pay']),
  deliveryDate: PropTypes.string.isRequired,
  period: PropTypes.string.isRequired,
  orderTotalCost: PropTypes.string,
  orderSubtotal: PropTypes.string,
  orderTotalTax: PropTypes.string,
  pricingList: PropTypes.shape({
    puptainerLabel: PropTypes.string.isRequired,
    shippingLabel: PropTypes.string.isRequired,
    scooperLabel: PropTypes.string.isRequired,
    bowlLabel: PropTypes.string,
    freeItems: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
      }),
    ),
  }),
  plans: PropTypes.arrayOf(
    PropTypes.shape({
      petId: PropTypes.string,
      petName: PropTypes.string,
      productType: PropTypes.string,
    }),
  ),
};
